/* __next_internal_client_entry_do_not_use__ Default auto */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Local
import { withStandardComponentWrapper } from 'helpers/HOC';
import { tv } from 'tailwind-variants';
import { getTestProps } from 'lib/testing/utils';
import { RemixIcon } from 'helpers/RemixIcon';
import { PlaceholderWrapper } from 'helpers/SitecoreWrappers/PlaceholderWrapper/PlaceholderWrapper';
import { useSitecoreContext } from 'lib/hooks/useSitecoreContext';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { BrandAndThemeProvider } from 'lib/context/BrandAndThemeContext';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { getContentGroups } from 'lib/utils/gtm-utils';
import { usePathname } from 'next/navigation';
import { sendGTMEvent } from '@next/third-parties/google';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
import useIsEditing from 'lib/hooks/useIsEditing';
const ContactUsContainer = (props)=>{
    const isEditing = useIsEditing();
    const { RenderingIdentifier, DynamicPlaceholderId } = props?.params || {};
    const phKey = `formcontainer-${DynamicPlaceholderId}`;
    const theme = props?.params?.selectTheme;
    const pathname = usePathname();
    const { sitecoreContext } = useSitecoreContext();
    const { base, titleArea, sectionTitle, content, heading, description, divider, iconCard, iconContainer, iconCardData, iconCardTitle, iconCardText, formContainer } = TAILWIND_VARIANTS();
    const languages = sitecoreContext?.languages;
    const languageCodes = languages?.map((lang)=>lang.isoCode.toLowerCase()) ?? [];
    // Extract content groups from pathname
    const { content_group, content_group2 } = getContentGroups(pathname ?? '', languageCodes);
    // Handler to get dynamic phone number from form field
    const handlePhoneLinkClick = (e)=>{
        const infinity_number = e.currentTarget.href.split('tel:')[1];
        // Send GTM event with dynamic phone number
        const gtmEventData = {
            event: 'call_to_click',
            'gtm.element.dataset.gtmComponentName': props?.rendering?.componentName,
            infinity_number: infinity_number || '',
            content_group: content_group,
            content_group2: content_group2
        };
        sendGTMEvent(gtmEventData);
    };
    /*
   * RENDERING
   */ return /*#__PURE__*/ _jsx(BrandAndThemeProvider, {
        theme: theme,
        children: /*#__PURE__*/ _jsxs("div", {
            "data-component": "authorable/shared/layout/contact-us-container",
            ...editingHydrationProps(isEditing),
            id: RenderingIdentifier,
            className: base(),
            ...getTestProps(`component-contact-us-container-${props?.rendering?.uid}`),
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: titleArea(),
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: sectionTitle(),
                            children: /*#__PURE__*/ _jsxs("div", {
                                className: content(),
                                children: [
                                    /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                        className: heading(),
                                        field: props?.fields?.title,
                                        tag: "h2"
                                    }),
                                    /*#__PURE__*/ _jsx(RichTextWrapper, {
                                        className: description(),
                                        field: props?.fields?.description
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: divider()
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsxs(LinkWrapper, {
                            ctaVariant: "custom",
                            field: props?.fields?.iconLink,
                            ctaComponentClass: "component-card-item-button-2-color",
                            ...getTestProps(`secondary-cta`),
                            suppressNewTabIcon: true,
                            className: iconCard(),
                            onClick: handlePhoneLinkClick,
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: iconContainer(),
                                    children: /*#__PURE__*/ _jsx(RemixIcon, {
                                        className: "flex",
                                        name: 'phone-line',
                                        size: "md"
                                    })
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: iconCardData(),
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            className: iconCardTitle(),
                                            "aria-hidden": "true",
                                            children: /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                                className: iconCardTitle(),
                                                field: props?.fields?.iconLabel,
                                                tag: "p"
                                            })
                                        }),
                                        props?.fields?.iconLink && props?.fields?.iconLink?.value?.text && /*#__PURE__*/ _jsx("p", {
                                            className: iconCardText(),
                                            children: props?.fields?.iconLink?.value?.text
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: formContainer(),
                    children: /*#__PURE__*/ _jsx(PlaceholderWrapper, {
                        name: phKey,
                        rendering: props?.rendering
                    })
                })
            ]
        })
    });
};
export const Default = withStandardComponentWrapper(ContactUsContainer, false);
const TAILWIND_VARIANTS = tv({
    slots: {
        base: [
            'base',
            'bg-component-section-bg',
            'flex',
            'flex-col',
            'gap-component-section-container-gutter',
            'py-component-section-container-padding-y',
            'lg:flex-row',
            'relative',
            'scroll-mt-[64px] md:scroll-mt-0'
        ],
        titleArea: [
            'flex',
            'flex-col',
            'gap-spacing-spacing-40',
            'lg:flex-1'
        ],
        sectionTitle: [
            'flex',
            'flex-col',
            'gap-space-between-base'
        ],
        content: [
            'flex',
            'flex-col',
            'gap-space-between-base'
        ],
        heading: [
            'font-typography-font-family-header',
            'text-typography-font-size-xlarge',
            'leading-typography-line-height-xlarge',
            'font-normal',
            'text-component-section-title',
            'w-full'
        ],
        description: [
            'font-typography-font-family-body',
            'text-typography-font-size-body-large',
            'leading-typography-line-height-body-large',
            'text-component-Accreditations-Reviews-body',
            'max-w-[1100px]'
        ],
        divider: [
            'bg-component-section-divider',
            'h-component-section-divider-height',
            'w-component-section-divider-width'
        ],
        iconCard: [
            'flex',
            'bg-color-grayscale-white',
            'gap-spacing-spacing-16',
            'items-start',
            'p-spacing-spacing-12',
            'rounded-border-radius-radius-2',
            'shadow-[0px_0px_8px_0px_rgba(0,0,0,0.1)]',
            'hover:bg-component-button-primary-inverse-bg-hover',
            'focus:bg-component-button-primary-inverse-bg-hover',
            'group'
        ],
        iconContainer: [
            'group-hover:bg-component-button-primary-inverse-icon-hover',
            'group-focus:bg-component-button-primary-inverse-icon-hover',
            'bg-color-grayscale-100',
            'flex',
            'gap-spacing-spacing-10',
            'items-center',
            'justify-center',
            'p-spacing-spacing-16',
            'rounded-border-radius-radius-2',
            'shrink-0'
        ],
        iconCardData: [
            'flex',
            'flex-col',
            'font-typography-font-family-body',
            'text-color-grayscale-black',
            'group-hover:text-color-text-white',
            'group-focus:text-color-text-white',
            'flex-1'
        ],
        iconCardTitle: [
            'flex',
            'flex-col',
            'justify-center',
            'text-typography-font-size-body-medium',
            'leading-typography-line-height-body-medium'
        ],
        iconCardText: [
            'text-typography-font-size-body-large',
            'leading-typography-line-height-body-large'
        ],
        formContainer: [
            'bg-color-grayscale-white',
            'flex',
            'flex-col',
            'gap-general-spacing-gutter',
            'p-padding-base',
            'rounded-general-border-radius-container',
            'shadow-[0px_0px_8px_0px_rgba(0,0,0,0.1)]',
            'lg:flex-1'
        ]
    },
    variants: {
        backgroundLayout: {
            FullWidth: {
                base: [
                    'before:z-[-1]',
                    'before:h-full',
                    'before:absolute',
                    'before:bg-component-section-bg',
                    'before:top-0',
                    'before:left-[calc(-50vw+50%)]',
                    'before:right-[calc(-50vw+50%)]'
                ]
            },
            Content: {
                base: []
            }
        }
    },
    defaultVariants: {
        backgroundLayout: 'FullWidth'
    }
});
